import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, RotateCcw, ImageOff, LayoutGrid, Layers, Undo2, ExternalLink } from "lucide-react";

/* Tinder-style review for video_asset_queue rows in status='generated'.
   Swipe right (or →) = approved. Swipe left (or ←) = rejected + reason, which
   requeues the asset for the /video-assets worker with the reason appended. */

const T = {
  navy: "#002868",
  red: "#B22234",
  green: "#2b5e49",
  gold: "#C5A55A",
  text: "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border: "rgba(27,27,27,0.12)",
  cardBg: "#FFFFFF",
  shadowLg: "0 12px 48px rgba(0,0,0,0.15)",
};

const REASON_CHIPS = [
  "wrong style",
  "bad anatomy",
  "background not keyable",
  "off-brand",
  "text artifacts",
  "cropped / out of frame",
];

type Asset = {
  id: string;
  asset_name: string;
  asset_type: string;
  prompt: string;
  chroma: string;
  status: string;
  image_url: string | null;
  rejection_reason: string | null;
  attempt: number;
  generation_notes: string | null;
};

type Counts = Record<string, number>;

/* Gallery filters. 'all' first so the default view answers "what do we have?" */
const GALLERY_TABS: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "approved", label: "Approved" },
  { key: "generated", label: "To review" },
  { key: "rejected", label: "Rejected" },
  { key: "pending", label: "Not generated" },
];

export function AssetReviewManager() {
  const [mode, setMode] = useState<"review" | "gallery">("review");
  const [all, setAll] = useState<Asset[]>([]);
  const [galleryTab, setGalleryTab] = useState("approved");
  const [queue, setQueue] = useState<Asset[]>([]);
  const [counts, setCounts] = useState<Counts>({});
  const [rejecting, setRejecting] = useState<Asset | null>(null);
  const [reason, setReason] = useState("");
  const [drag, setDrag] = useState(0);
  const dragStart = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("video_asset_queue")
      .select("*")
      .eq("status", "generated")
      .order("updated_at", { ascending: true });
    setQueue((data as Asset[]) || []);
    /* Full set drives both the counts and the gallery, so one fetch serves both. */
    const { data: everything } = await supabase
      .from("video_asset_queue")
      .select("*")
      .order("updated_at", { ascending: false });
    const rows = (everything as Asset[]) || [];
    setAll(rows);
    const c: Counts = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    setCounts(c);
  }, []);

  /* Direct status set — used by the gallery to undo a mis-swipe without
     needing the asset to come back around in the review queue. */
  const setStatus = useCallback(async (asset: Asset, status: string) => {
    setBusy(true);
    await supabase
      .from("video_asset_queue")
      .update({
        status,
        reviewed_at: new Date().toISOString(),
        ...(status === "rejected" ? {} : { rejection_reason: null }),
      })
      .eq("id", asset.id);
    setBusy(false);
    load();
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const current = queue[0] || null;

  const decide = useCallback(async (asset: Asset, approved: boolean, why?: string) => {
    setBusy(true);
    await supabase
      .from("video_asset_queue")
      .update(
        approved
          ? { status: "approved", reviewed_at: new Date().toISOString(), rejection_reason: null }
          : { status: "rejected", reviewed_at: new Date().toISOString(), rejection_reason: why || "rejected" }
      )
      .eq("id", asset.id);
    setBusy(false);
    setRejecting(null);
    setReason("");
    setDrag(0);
    load();
  }, [load]);

  /* keyboard: → approve, ← open reject */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!current || rejecting || busy) return;
      if (e.key === "ArrowRight") decide(current, true);
      if (e.key === "ArrowLeft") setRejecting(current);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, rejecting, busy, decide]);

  /* pointer drag */
  const onPointerDown = (e: React.PointerEvent) => {
    dragStart.current = e.clientX;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragStart.current !== null) setDrag(e.clientX - dragStart.current);
  };
  const onPointerUp = () => {
    if (dragStart.current === null || !current) return;
    const d = drag;
    dragStart.current = null;
    if (d > 120) decide(current, true);
    else if (d < -120) { setRejecting(current); setDrag(0); }
    else setDrag(0);
  };

  const chip = (label: string, value: number, color: string) => (
    <span key={label} style={{
      fontSize: 12, padding: "3px 10px", borderRadius: 999, marginRight: 8,
      background: `${color}14`, color, border: `1px solid ${color}33`, fontWeight: 600,
    }}>{label}: {value}</span>
  );

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "8px 0 40px" }}>
      <div style={{ marginBottom: 18 }}>
        {chip("pending", counts.pending || 0, T.navy)}
        {chip("generating", counts.generating || 0, T.gold)}
        {chip("to review", counts.generated || 0, T.red)}
        {chip("approved", counts.approved || 0, T.green)}
        {chip("rejected", counts.rejected || 0, T.textMuted.replace(/[^#\w(),. ]/g, "") || "#666")}
      </div>

      {/* Review ⇄ Gallery. Review is the working queue; gallery is the library
          view — what's approved, and a way back from an accidental reject. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {([
          { key: "review", label: "Review queue", Icon: Layers },
          { key: "gallery", label: "Asset library", Icon: LayoutGrid },
        ] as const).map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            style={{
              display: "flex", alignItems: "center", gap: 7, cursor: "pointer",
              fontSize: 13.5, fontWeight: 600, padding: "7px 14px", borderRadius: 8,
              border: `1px solid ${mode === key ? T.navy : T.border}`,
              background: mode === key ? `${T.navy}0f` : "transparent",
              color: mode === key ? T.navy : T.textMuted,
            }}
          >
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {mode === "gallery" && (
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {GALLERY_TABS.map(({ key, label }) => {
              const n = key === "all" ? all.length : (counts[key] || 0);
              const on = galleryTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setGalleryTab(key)}
                  style={{
                    cursor: "pointer", fontSize: 12.5, fontWeight: 600, padding: "5px 11px",
                    borderRadius: 999, border: `1px solid ${on ? T.navy : T.border}`,
                    background: on ? T.navy : "transparent", color: on ? "#fff" : T.textMuted,
                  }}
                >{label} ({n})</button>
              );
            })}
          </div>

          {(() => {
            const shown = all.filter((a) => galleryTab === "all" || a.status === galleryTab);
            if (!shown.length) {
              return (
                <div style={{
                  border: `1.5px dashed ${T.border}`, borderRadius: 12, padding: "48px 24px",
                  textAlign: "center", color: T.textMuted,
                }}>
                  <ImageOff size={26} style={{ opacity: 0.4, marginBottom: 8 }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>Nothing here yet.</p>
                </div>
              );
            }
            return (
              <div style={{
                display: "grid", gap: 14,
                gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
              }}>
                {shown.map((a) => (
                  <div key={a.id} style={{
                    border: `1px solid ${T.border}`, borderRadius: 11, overflow: "hidden",
                    background: T.cardBg, display: "flex", flexDirection: "column",
                  }}>
                    <div style={{
                      background: a.chroma === "green" ? "#00b140" : "#efe9d8",
                      minHeight: 130, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {a.image_url
                        ? <img src={a.image_url} alt={a.asset_name} loading="lazy"
                               style={{ width: "100%", maxHeight: 190, objectFit: "contain", display: "block" }} />
                        : <span style={{ color: T.textMuted, fontSize: 12.5 }}>not generated</span>}
                    </div>
                    <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 7, flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, wordBreak: "break-word", color: T.text }}>
                        {a.asset_name}
                      </div>
                      <div style={{ fontSize: 11.5, color: T.textMuted }}>
                        {a.status}{a.attempt ? ` · attempt ${a.attempt}` : ""}
                      </div>
                      {a.rejection_reason && (
                        <div style={{ fontSize: 11.5, color: T.red }}>{a.rejection_reason}</div>
                      )}
                      <div style={{ display: "flex", gap: 6, marginTop: "auto", flexWrap: "wrap" }}>
                        {a.status !== "approved" && a.image_url && (
                          <button type="button" disabled={busy} onClick={() => setStatus(a, "approved")}
                            style={{
                              cursor: "pointer", fontSize: 11.5, fontWeight: 600, padding: "4px 9px",
                              borderRadius: 6, border: `1px solid ${T.green}44`,
                              background: `${T.green}12`, color: T.green,
                            }}>
                            <Check size={11} style={{ verticalAlign: -1 }} /> Approve
                          </button>
                        )}
                        {a.status === "rejected" && (
                          <button type="button" disabled={busy} onClick={() => setStatus(a, "generated")}
                            style={{
                              cursor: "pointer", fontSize: 11.5, fontWeight: 600, padding: "4px 9px",
                              borderRadius: 6, border: `1px solid ${T.navy}44`,
                              background: `${T.navy}12`, color: T.navy,
                            }}>
                            <Undo2 size={11} style={{ verticalAlign: -1 }} /> Un-reject
                          </button>
                        )}
                        {a.status === "approved" && (
                          <button type="button" disabled={busy} onClick={() => setStatus(a, "generated")}
                            style={{
                              cursor: "pointer", fontSize: 11.5, fontWeight: 600, padding: "4px 9px",
                              borderRadius: 6, border: `1px solid ${T.border}`,
                              background: "transparent", color: T.textMuted,
                            }}>
                            <RotateCcw size={11} style={{ verticalAlign: -1 }} /> Re-review
                          </button>
                        )}
                        {a.image_url && (
                          <a href={a.image_url} target="_blank" rel="noopener noreferrer"
                            style={{
                              fontSize: 11.5, fontWeight: 600, padding: "4px 9px", borderRadius: 6,
                              border: `1px solid ${T.border}`, color: T.textMuted, textDecoration: "none",
                            }}>
                            <ExternalLink size={11} style={{ verticalAlign: -1 }} /> Full
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {mode === "review" && !current && (
        <div style={{
          border: `1.5px dashed ${T.border}`, borderRadius: 12, padding: "64px 24px",
          textAlign: "center", color: T.textMuted,
        }}>
          <ImageOff size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Nothing waiting for review.</p>
          <p style={{ margin: "6px 0 0", fontSize: 13.5 }}>
            Run <code>/video-assets</code> in the amh-dev pane to generate the next
            {" "}{counts.pending || 0} pending / {counts.rejected || 0} rejected assets.
          </p>
        </div>
      )}

      {mode === "review" && current && (
        <div>
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={{
              background: T.cardBg, borderRadius: 14, boxShadow: T.shadowLg,
              border: `1px solid ${T.border}`, overflow: "hidden", cursor: "grab",
              transform: `translateX(${drag}px) rotate(${drag / 30}deg)`,
              transition: dragStart.current === null ? "transform .18s" : "none",
              touchAction: "pan-y", userSelect: "none", position: "relative",
            }}
          >
            {/* swipe verdict ghosts */}
            <div style={{
              position: "absolute", top: 18, left: 18, zIndex: 2, padding: "4px 14px",
              border: `3px solid ${T.green}`, color: T.green, borderRadius: 8,
              fontWeight: 800, letterSpacing: 2, transform: "rotate(-12deg)",
              opacity: Math.max(0, Math.min(1, drag / 120)),
            }}>APPROVE</div>
            <div style={{
              position: "absolute", top: 18, right: 18, zIndex: 2, padding: "4px 14px",
              border: `3px solid ${T.red}`, color: T.red, borderRadius: 8,
              fontWeight: 800, letterSpacing: 2, transform: "rotate(12deg)",
              opacity: Math.max(0, Math.min(1, -drag / 120)),
            }}>REJECT</div>

            <div style={{
              background: current.chroma === "green" ? "#00b140" : "#efe9d8",
              display: "flex", alignItems: "center", justifyContent: "center", minHeight: 340,
            }}>
              {current.image_url
                ? <img src={current.image_url} alt={current.asset_name} draggable={false}
                       style={{ maxWidth: "100%", maxHeight: 480, display: "block" }} />
                : <span style={{ color: "#fff", fontWeight: 600 }}>no image_url</span>}
            </div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                <strong style={{ fontSize: 15 }}>{current.asset_name}</strong>
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  {current.asset_type} · attempt {current.attempt} · {queue.length} in queue
                </span>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 12.5, lineHeight: 1.5, color: T.textMuted,
                          maxHeight: 72, overflow: "auto" }}>
                {current.prompt}
              </p>
              {current.generation_notes && (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: T.gold }}>⚙ {current.generation_notes}</p>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 16 }}>
            <button onClick={() => setRejecting(current)} disabled={busy} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 22px",
              borderRadius: 999, border: `1.5px solid ${T.red}`, background: "#fff",
              color: T.red, fontWeight: 700, cursor: "pointer",
            }}><X size={16} /> Reject (←)</button>
            <button onClick={() => decide(current, true)} disabled={busy} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "10px 22px",
              borderRadius: 999, border: "none", background: T.green, color: "#fff",
              fontWeight: 700, cursor: "pointer",
            }}><Check size={16} /> Approve (→)</button>
          </div>
        </div>
      )}

      {/* reject reason modal */}
      {rejecting && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 60,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => { setRejecting(null); setReason(""); }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 12, padding: 22, width: "100%", maxWidth: 460,
            boxShadow: T.shadowLg,
          }}>
            <strong style={{ fontSize: 15 }}>Why is {rejecting.asset_name} rejected?</strong>
            <p style={{ margin: "4px 0 12px", fontSize: 12.5, color: T.textMuted }}>
              The reason is appended to the prompt when the worker retries — be specific.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {REASON_CHIPS.map(c => (
                <button key={c} onClick={() => setReason(r => r ? `${r}; ${c}` : c)} style={{
                  fontSize: 12.5, padding: "5px 12px", borderRadius: 999,
                  border: `1px solid ${reason.includes(c) ? T.red : T.border}`,
                  background: reason.includes(c) ? `${T.red}11` : "#fff",
                  color: reason.includes(c) ? T.red : T.text, cursor: "pointer",
                }}>{c}</button>
              ))}
            </div>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              placeholder="…or type exactly what to fix"
              style={{ width: "100%", minHeight: 70, padding: 10, borderRadius: 8,
                       border: `1px solid ${T.border}`, fontSize: 13.5, resize: "vertical" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
              <button onClick={() => { setRejecting(null); setReason(""); }} style={{
                padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`,
                background: "#fff", cursor: "pointer",
              }}><RotateCcw size={13} style={{ verticalAlign: -2 }} /> Cancel</button>
              <button disabled={!reason.trim() || busy}
                onClick={() => decide(rejecting, false, reason.trim())} style={{
                  padding: "8px 18px", borderRadius: 8, border: "none", background: T.red,
                  color: "#fff", fontWeight: 700, cursor: "pointer",
                  opacity: !reason.trim() || busy ? 0.5 : 1,
                }}>Reject & requeue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
